import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, Calendar, Eye, Filter, X } from 'lucide-react';
import PaiementModal from './PaiementModal';
import { API_URL } from '../../services/api';

function PaiementsFournisseurs() {
  const [achatsCredits, setAchatsCredits] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAchat, setSelectedAchat] = useState(null);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [filterFournisseur, setFilterFournisseur] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les fournisseurs
      const fournisseursRes = await fetch(`${API_URL}/fournisseurs`);
      const fournisseursData = await fournisseursRes.json();
      setFournisseurs(Array.isArray(fournisseursData) ? fournisseursData : []);

      // Charger les achats en crédit
      const achatsRes = await fetch(`${API_URL}/paiements/achats/credits`);
      const achatsData = await achatsRes.json();
      setAchatsCredits(Array.isArray(achatsData) ? achatsData : []);
    } catch (error) {
      console.error('Erreur:', error);
      setFournisseurs([]);
      setAchatsCredits([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePayer = (achat) => {
    setSelectedAchat(achat);
    setShowPaiementModal(true);
  };

  const handlePaiementEnregistre = () => {
    loadData();
  };

  const filteredAchats = achatsCredits.filter(achat => {
    if (filterFournisseur && achat.fournisseur_id !== parseInt(filterFournisseur)) {
      return false;
    }
    return true;
  });

  const totalSolde = filteredAchats.reduce((sum, achat) => sum + parseFloat(achat.solde_a_regler || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-3">
          <CreditCard className="h-8 w-8 text-green-600" />
          <span>Paiements Fournisseurs</span>
        </h2>
        <div className="w-full sm:w-auto bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-lg shadow-lg">
          <div className="text-sm opacity-90">Solde total à régler</div>
          <div className="text-2xl font-bold">{totalSolde.toFixed(2)} DH</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center space-x-2 shrink-0">
          <Filter className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-700">Filtrer :</span>
        </div>
        <select
          value={filterFournisseur}
          onChange={(e) => setFilterFournisseur(e.target.value)}
          className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">Tous les fournisseurs</option>
          {fournisseurs.map(f => (
            <option key={f.id} value={f.id}>{f.nom}</option>
          ))}
        </select>
        {filterFournisseur && (
          <button
            onClick={() => setFilterFournisseur('')}
            className="w-full sm:w-auto px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-center"
          >
            Réinitialiser
          </button>
        )}
        <div className="sm:ml-auto text-sm text-gray-600">
          {filteredAchats.length} achat(s) en crédit
        </div>
      </div>

      {/* Liste des achats en crédit */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      ) : filteredAchats.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <CreditCard className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Aucun achat en crédit</p>
          <p className="text-sm text-gray-500 mt-2">Tous vos achats sont payés !</p>
        </div>
      ) : (
        <>
        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {filteredAchats.map((achat) => {
            const prixTotal = parseFloat(achat.prix_achat_total || 0);
            const montantPaye = parseFloat(achat.montant_paye || 0);
            const solde = parseFloat(achat.solde_a_regler || 0);
            const pourcentagePaye = prixTotal > 0 ? (montantPaye / prixTotal) * 100 : 0;
            return (
              <div key={achat.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-bold text-gray-900">{achat.reference}</div>
                    <div className="text-sm text-gray-600">{achat.fournisseur_nom || 'N/A'}</div>
                  </div>
                  <span className="font-bold text-lg text-red-600 shrink-0">{solde.toFixed(2)} DH</span>
                </div>
                <div className="text-xs text-gray-500 mb-2">{achat.designation}</div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${pourcentagePaye}%` }}></div>
                  </div>
                  <span className="text-xs text-gray-500 shrink-0">{pourcentagePaye.toFixed(0)}%</span>
                </div>
                <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
                  <span>Total: {prixTotal.toFixed(2)} DH</span>
                  <span className="text-green-600">Payé: {montantPaye.toFixed(2)} DH</span>
                  {achat.date_achat && <span>{new Date(achat.date_achat).toLocaleDateString('fr-FR')}</span>}
                </div>
                <button
                  onClick={() => handlePayer(achat)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center space-x-2 shadow-md"
                >
                  <DollarSign className="h-4 w-4" />
                  <span>Payer</span>
                </button>
              </div>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Achat</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Désignation</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix Total</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payé</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Solde</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernier Paiement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Moyen</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAchats.map((achat) => {
                const prixTotal = parseFloat(achat.prix_achat_total || 0);
                const montantPaye = parseFloat(achat.montant_paye || 0);
                const solde = parseFloat(achat.solde_a_regler || 0);
                const pourcentagePaye = prixTotal > 0 ? (montantPaye / prixTotal) * 100 : 0;

                return (
                  <tr key={achat.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {achat.reference}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {achat.date_achat ? new Date(achat.date_achat).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {achat.fournisseur_nom || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {achat.designation}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                      {prixTotal.toFixed(2)} DH
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <div className="space-y-1">
                        <div className="font-semibold text-green-600">
                          {montantPaye.toFixed(2)} DH
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all"
                            style={{ width: `${pourcentagePaye}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {pourcentagePaye.toFixed(0)}%
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <span className="font-bold text-lg text-red-600">
                        {solde.toFixed(2)} DH
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {achat.date_dernier_paiement ? (
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-blue-500" />
                          <span>{new Date(achat.date_dernier_paiement).toLocaleDateString('fr-FR')}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Aucun paiement</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {achat.moyen_paiement ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          achat.moyen_paiement === 'virement' ? 'bg-blue-100 text-blue-700' :
                          achat.moyen_paiement === 'cheque' ? 'bg-purple-100 text-purple-700' :
                          achat.moyen_paiement === 'especes' ? 'bg-green-100 text-green-700' :
                          achat.moyen_paiement === 'carte' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {achat.moyen_paiement === 'virement' ? '🏦 Virement' :
                           achat.moyen_paiement === 'cheque' ? '📝 Chèque' :
                           achat.moyen_paiement === 'especes' ? '💵 Espèces' :
                           achat.moyen_paiement === 'carte' ? '💳 Carte' :
                           achat.moyen_paiement}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic text-xs">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <button
                        onClick={() => handlePayer(achat)}
                        className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all flex items-center space-x-2 mx-auto shadow-md hover:shadow-lg"
                      >
                        <DollarSign className="h-4 w-4" />
                        <span>Payer</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
      )}

      {/* Modal de paiement */}
      {showPaiementModal && selectedAchat && (
        <PaiementModal
          achat={selectedAchat}
          fournisseur={fournisseurs.find(f => f.id === selectedAchat.fournisseur_id)}
          onClose={() => {
            setShowPaiementModal(false);
            setSelectedAchat(null);
          }}
          onPaiementEnregistre={handlePaiementEnregistre}
        />
      )}
    </div>
  );
}

export default PaiementsFournisseurs;
